// Create and edit form for a scheduled job. Used by SchedulerJobFormPage (create)
// and by the job detail page (edit).
import React, { useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiErrorCode, getApiErrorField } from '@/client/utils/errors';
import {
  useCreateJobMutation,
  useSchedulerMetaQuery,
  useUpdateJobMutation,
} from '@/client/api/scheduler/scheduler.query';
import { JobTiming, JobWrite, SchedulerJob } from '@/client/types/scheduler';
import {
  Card,
  DAY_NAMES,
  ErrorNotice,
  INPUT_CLASS,
  describeTiming,
  schedulerErrorText,
  stepOf,
} from './schedulerUi';

const MASK = '********';
const INTERVALS = [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30];

/* ---------- schedule builder ---------- */

type Preset = 'interval' | 'hourly' | 'daily' | 'weekly' | 'demand' | 'advanced';
const PRESETS: { id: Preset; label: string }[] = [
  { id: 'interval', label: 'Every N minutes' },
  { id: 'hourly', label: 'Hourly' },
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'demand', label: 'On demand' },
  { id: 'advanced', label: 'Advanced' },
];
const RAW_KEYS = ['minutes', 'hours', 'days', 'months', 'weekdays'] as const;
type RawKey = (typeof RAW_KEYS)[number];

interface Sched {
  preset: Preset;
  every: number;
  minute: number;
  time: string;
  weekdays: number[];
  raw: Record<RawKey, string>;
}

const pad = (n: number) => String(n).padStart(2, '0');
const range = (step: number) => Array.from({ length: 60 / step }, (_, i) => i * step);
const emptyRaw = (): Sched['raw'] => ({
  minutes: '',
  hours: '',
  days: '',
  months: '',
  weekdays: '',
});

const defaultSched = (): Sched => ({
  preset: 'demand',
  every: 10,
  minute: 0,
  time: '02:00',
  weekdays: [1],
  raw: emptyRaw(),
});

const fromTiming = (t: false | JobTiming): Sched => {
  const base = defaultSched();
  if (!t) return base;
  const raw = emptyRaw();
  RAW_KEYS.forEach((k) => {
    raw[k] = (t[k] ?? []).join(', ');
  });
  const advanced: Sched = { ...base, preset: 'advanced', raw };
  const { minutes = [], hours = [], days = [], months = [], weekdays = [] } = t;
  if (days.length || months.length || (t.years ?? []).length) return advanced;
  if (!hours.length && !weekdays.length) {
    const step = stepOf(minutes, 60);
    if (step && INTERVALS.includes(step)) return { ...base, preset: 'interval', every: step, raw };
    if (minutes.length === 1) return { ...base, preset: 'hourly', minute: minutes[0], raw };
    return advanced;
  }
  if (minutes.length === 1 && hours.length === 1) {
    const time = `${pad(hours[0])}:${pad(minutes[0])}`;
    if (!weekdays.length) return { ...base, preset: 'daily', time, raw };
    return { ...base, preset: 'weekly', time, weekdays, raw };
  }
  return advanced;
};

const parseList = (text: string, label: string, lo: number, hi: number) => {
  const parts = text
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  const nums = parts.map(Number);
  if (nums.some((n) => !Number.isInteger(n) || n < lo || n > hi)) {
    return { error: `${label} must be whole numbers from ${lo} to ${hi}, separated by commas` };
  }
  return { list: Array.from(new Set(nums)).sort((a, b) => a - b) };
};

const toTiming = (s: Sched): { timing?: false | JobTiming; error?: string } => {
  const [h, m] = s.time.split(':').map(Number);
  switch (s.preset) {
    case 'demand':
      return { timing: false };
    case 'interval':
      return { timing: { minutes: range(s.every) } };
    case 'hourly':
      return { timing: { minutes: [s.minute] } };
    case 'daily':
      return { timing: { minutes: [m], hours: [h] } };
    case 'weekly':
      return s.weekdays.length
        ? { timing: { minutes: [m], hours: [h], weekdays: [...s.weekdays].sort() } }
        : { error: 'Pick at least one day' };
    default: {
      const bounds: Record<RawKey, [number, number]> = {
        minutes: [0, 59],
        hours: [0, 23],
        days: [1, 31],
        months: [1, 12],
        weekdays: [0, 6],
      };
      const timing: JobTiming = {};
      for (const k of RAW_KEYS) {
        if (!s.raw[k].trim()) continue;
        const r = parseList(s.raw[k], k, bounds[k][0], bounds[k][1]);
        if (r.error) return { error: r.error };
        timing[k] = r.list;
      }
      return { timing };
    }
  }
};

/* ---------- form state ---------- */

interface HeaderRow {
  name: string;
  value: string;
  /** An existing secret; an empty value means keep the stored one. */
  secret: boolean;
}

const numStr = (n: number | undefined, fallback: number) => String(n ?? fallback);

const FormField: React.FC<{
  label: string;
  error?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ label, error, hint, children, className = '' }) => (
  <label className={`block text-sm ${className}`}>
    <span className="font-medium text-gray-700">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
    {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
  </label>
);

const TEXTAREA_CLASS =
  'w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-blue-400';

const JobForm: React.FC<{
  job?: SchedulerJob;
  onSaved: (job: SchedulerJob) => void;
  onCancel: () => void;
}> = ({ job, onSaved, onCancel }) => {
  const edit = !!job;
  const metaQ = useSchedulerMetaQuery();
  const create = useCreateJobMutation();
  const update = useUpdateJobMutation();
  const meta = metaQ.data;

  const [title, setTitle] = useState(job?.title ?? '');
  const [category, setCategory] = useState(job?.category ?? '');
  const [notes, setNotes] = useState(job?.notes ?? '');
  const [method, setMethod] = useState(job?.params.method ?? 'GET');
  const [url, setUrl] = useState(job?.params.url ?? '');
  const [headers, setHeaders] = useState<HeaderRow[]>(
    (job?.params.headers ?? []).map((h) => ({
      name: h.name,
      value: h.secret ? '' : h.value,
      secret: h.secret,
    })),
  );
  const [data, setData] = useState(job?.params.data ?? '');
  const [successMatch, setSuccessMatch] = useState(job?.params.success_match ?? '');
  const [errorMatch, setErrorMatch] = useState(job?.params.error_match ?? '');
  const [reqTimeout, setReqTimeout] = useState(numStr(job?.params.timeout, 30));
  const [sched, setSched] = useState<Sched>(job ? fromTiming(job.timing) : defaultSched());
  const [timezone, setTimezone] = useState(job?.timezone ?? 'GMT');
  const [timeout, setTimeoutS] = useState(numStr(job?.timeout, 300));
  const [retries, setRetries] = useState(numStr(job?.retries, 0));
  const [retryDelay, setRetryDelay] = useState(numStr(job?.retry_delay, 0));
  const [maxChildren, setMaxChildren] = useState(numStr(job?.max_children, 1));
  const [catchUp, setCatchUp] = useState(job?.catch_up === 1);
  const [target, setTarget] = useState(job?.target ?? 'allgrp');
  const [startEnabled, setStartEnabled] = useState(false);
  const [reason, setReason] = useState('');
  const [local, setLocal] = useState<Record<string, string>>({});

  const mutation = edit ? update : create;
  const serverFields = getApiErrorField<Record<string, string>>(mutation.error, 'fields') ?? {};
  const errs: Record<string, string> = {
    ...(getApiErrorCode(mutation.error) === 'missing_reason'
      ? { reason: 'A reason is required' }
      : {}),
    ...serverFields,
    ...local,
  };
  const hasFieldErrors = Object.keys(errs).length > 0;

  const built = useMemo(() => toTiming(sched), [sched]);
  const summary = built.error ? built.error : describeTiming(built.timing);
  const showBody = ['POST', 'PUT', 'PATCH'].includes(method);
  const methods = meta?.methods ?? ['GET', 'POST'];

  const setS = (patch: Partial<Sched>) => setSched((s) => ({ ...s, ...patch }));
  const setHeader = (i: number, patch: Partial<HeaderRow>) =>
    setHeaders((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const isMasked = (r: HeaderRow) => r.secret && r.value === '';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const problems: Record<string, string> = {};
    if (title.trim().length < 3 || title.trim().length > 120)
      problems.title = 'Use 3 to 120 characters';
    if (!url.trim()) problems['params.url'] = 'Enter the URL to call';
    if (built.error) problems.timing = built.error;
    if (headers.some((h) => !h.name.trim() || (!h.secret && !h.value.trim()))) {
      problems['params.headers'] = 'Every header needs a name and a value';
    }
    if (reason.trim().length < 5) problems.reason = 'Give a reason of at least 5 characters';
    setLocal(problems);
    if (Object.keys(problems).length) return;

    const body: JobWrite = {
      reason: reason.trim(),
      title: title.trim(),
      timing: built.timing,
      params: {
        method,
        url: url.trim(),
        // An untouched secret goes back masked and the server keeps the stored value.
        headers: headers.map((h) => ({ name: h.name.trim(), value: isMasked(h) ? MASK : h.value })),
        data: showBody ? data : '',
        success_match: successMatch,
        error_match: errorMatch,
        timeout: Number(reqTimeout),
      },
      category: category || undefined,
      target,
      timezone: timezone.trim(),
      timeout: Number(timeout),
      retries: Number(retries),
      retry_delay: Number(retryDelay),
      max_children: Number(maxChildren),
      catch_up: catchUp ? 1 : 0,
      notes,
    };
    if (job) {
      update.mutate({ id: job.id, body }, { onSuccess: (r) => onSaved(r.event) });
    } else {
      create.mutate(
        { ...body, enabled: startEnabled ? 1 : 0 },
        { onSuccess: (r) => onSaved(r.event) },
      );
    }
  };

  if (metaQ.isError) return <ErrorNotice error={metaQ.error} onRetry={() => metaQ.refetch()} />;

  const num = (v: string, set: (s: string) => void, min: number, max: number) => (
    <input
      type="number"
      min={min}
      max={max}
      value={v}
      onChange={(e) => set(e.target.value)}
      className={`${INPUT_CLASS} w-full`}
    />
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      <Card title="Basics">
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Title" error={errs.title} className="md:col-span-2">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`${INPUT_CLASS} w-full`}
            />
          </FormField>
          <FormField label="Category" error={errs.category}>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={`${INPUT_CLASS} w-full`}
            >
              <option value="">Default</option>
              {(meta?.categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Notes" error={errs.notes}>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${INPUT_CLASS} w-full`}
            />
          </FormField>
        </div>
      </Card>

      <Card title="Request">
        <div className="grid gap-4 md:grid-cols-4">
          <FormField label="Method" error={errs['params.method']}>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className={`${INPUT_CLASS} w-full`}
            >
              {methods.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            label="URL"
            error={errs['params.url']}
            className="md:col-span-3"
            hint={
              meta?.allowed_hosts?.length
                ? `HTTPS only. Allowed hosts: ${meta.allowed_hosts.join(', ')}`
                : 'HTTPS only.'
            }
          >
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={`${INPUT_CLASS} w-full font-mono`}
            />
          </FormField>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Headers</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setHeaders([...headers, { name: '', value: '', secret: false }])}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add header
            </Button>
          </div>
          {headers.length === 0 && <p className="mt-1 text-xs text-gray-500">No headers.</p>}
          <div className="mt-2 space-y-2">
            {headers.map((h, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={h.name}
                  onChange={(e) => setHeader(i, { name: e.target.value })}
                  placeholder="Name"
                  className={`${INPUT_CLASS} w-48 font-mono`}
                />
                <input
                  value={h.value}
                  type={h.secret ? 'password' : 'text'}
                  autoComplete="off"
                  onChange={(e) => setHeader(i, { value: e.target.value })}
                  placeholder={h.secret ? `${MASK} (leave blank to keep)` : 'Value'}
                  className={`${INPUT_CLASS} min-w-0 flex-1 font-mono`}
                />
                <button
                  type="button"
                  aria-label="Remove header"
                  onClick={() => setHeaders(headers.filter((_, j) => j !== i))}
                  className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          {errs['params.headers'] && (
            <p className="mt-1 text-xs text-red-600">{errs['params.headers']}</p>
          )}
        </div>

        {showBody && (
          <FormField label="Request body" error={errs['params.data']} className="mt-4">
            <textarea
              value={data}
              onChange={(e) => setData(e.target.value)}
              rows={4}
              className={`${TEXTAREA_CLASS} font-mono`}
            />
          </FormField>
        )}
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <FormField
            label="Success match"
            hint="Regex the response must match"
            error={errs['params.success_match']}
          >
            <input
              value={successMatch}
              onChange={(e) => setSuccessMatch(e.target.value)}
              className={`${INPUT_CLASS} w-full font-mono`}
            />
          </FormField>
          <FormField
            label="Error match"
            hint="Regex that marks the run failed"
            error={errs['params.error_match']}
          >
            <input
              value={errorMatch}
              onChange={(e) => setErrorMatch(e.target.value)}
              className={`${INPUT_CLASS} w-full font-mono`}
            />
          </FormField>
          <FormField label="Request timeout (s)" hint="1 to 86400" error={errs['params.timeout']}>
            {num(reqTimeout, setReqTimeout, 1, 86400)}
          </FormField>
        </div>
      </Card>

      <Card title="Schedule" note={<span className="font-medium text-gray-700">{summary}</span>}>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setS({ preset: p.id })}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                sched.preset === p.id
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          {sched.preset === 'interval' && (
            <FormField label="Every (minutes)">
              <select
                value={sched.every}
                onChange={(e) => setS({ every: Number(e.target.value) })}
                className={INPUT_CLASS}
              >
                {INTERVALS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          {sched.preset === 'hourly' && (
            <FormField label="At minute">
              <input
                type="number"
                min={0}
                max={59}
                value={sched.minute}
                onChange={(e) =>
                  setS({ minute: Math.min(59, Math.max(0, Number(e.target.value) || 0)) })
                }
                className={`${INPUT_CLASS} w-24`}
              />
            </FormField>
          )}
          {(sched.preset === 'daily' || sched.preset === 'weekly') && (
            <FormField label="At time">
              <input
                type="time"
                value={sched.time}
                onChange={(e) => e.target.value && setS({ time: e.target.value })}
                className={INPUT_CLASS}
              />
            </FormField>
          )}
          {sched.preset === 'weekly' && (
            <div className="flex gap-1">
              {DAY_NAMES.map((d, i) => {
                const on = sched.weekdays.includes(i);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() =>
                      setS({
                        weekdays: on
                          ? sched.weekdays.filter((x) => x !== i)
                          : [...sched.weekdays, i],
                      })
                    }
                    className={`h-9 rounded-lg border px-2 text-xs font-medium ${
                      on
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          )}
          {sched.preset === 'demand' && (
            <p className="text-sm text-gray-500">The job only runs when someone starts it.</p>
          )}
        </div>
        {sched.preset === 'advanced' && (
          <div className="mt-4 grid gap-3 md:grid-cols-5">
            {RAW_KEYS.map((k) => (
              <FormField
                key={k}
                label={k.charAt(0).toUpperCase() + k.slice(1)}
                hint={k === 'weekdays' ? '0 is Sunday' : 'Blank means every'}
              >
                <input
                  value={sched.raw[k]}
                  onChange={(e) => setS({ raw: { ...sched.raw, [k]: e.target.value } })}
                  placeholder="e.g. 0, 30"
                  className={`${INPUT_CLASS} w-full font-mono`}
                />
              </FormField>
            ))}
          </div>
        )}
        {errs.timing && <p className="mt-2 text-xs text-red-600">{errs.timing}</p>}
      </Card>

      <Card title="Limits">
        <div className="grid gap-4 md:grid-cols-4">
          <FormField label="Timezone" error={errs.timezone} hint="For example Africa/Accra or GMT">
            <input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className={`${INPUT_CLASS} w-full`}
            />
          </FormField>
          <FormField label="Job timeout (s)" error={errs.timeout} hint="1 to 86400">
            {num(timeout, setTimeoutS, 1, 86400)}
          </FormField>
          <FormField label="Retries" error={errs.retries} hint="0 to 32">
            {num(retries, setRetries, 0, 32)}
          </FormField>
          <FormField label="Retry delay (s)" error={errs.retry_delay} hint="0 to 86400">
            {num(retryDelay, setRetryDelay, 0, 86400)}
          </FormField>
          <FormField label="Max concurrent runs" error={errs.max_children} hint="0 to 32">
            {num(maxChildren, setMaxChildren, 0, 32)}
          </FormField>
          <FormField label="Target" error={errs.target}>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className={`${INPUT_CLASS} w-full`}
            >
              {Array.from(new Set([target, ...(meta?.targets ?? [])])).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </FormField>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={catchUp}
              onChange={(e) => setCatchUp(e.target.checked)}
            />
            Catch up on missed runs
          </label>
          {!edit && (
            <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={startEnabled}
                onChange={(e) => setStartEnabled(e.target.checked)}
              />
              Enable right away
            </label>
          )}
        </div>
        {!edit && !startEnabled && (
          <p className="mt-2 text-xs text-gray-500">New jobs start paused until you resume them.</p>
        )}
      </Card>

      <Card title="Reason">
        <FormField
          label={edit ? 'Why are you changing this job?' : 'Why are you creating this job?'}
          error={errs.reason}
        >
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className={TEXTAREA_CLASS}
          />
        </FormField>
      </Card>

      {!!mutation.error && !hasFieldErrors && (
        <p className="text-sm text-red-600">{schedulerErrorText(mutation.error)}</p>
      )}
      {hasFieldErrors && (
        <p className="text-sm text-red-600">Fix the highlighted fields and try again.</p>
      )}
      <div className="flex gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {edit ? 'Save changes' : 'Create job'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default JobForm;
